/**
 * Einfliegende Abschnitte und Karten.
 *
 * **Warum ein IntersectionObserver und nicht `animation-timeline: view()`?**
 * Der reine CSS-Weg war der erste Versuch und ist an zwei Stellen gescheitert:
 *
 * 1. Firefox und Safari kennen scroll-getriebene Animationen nicht – dort
 *    passierte schlicht nichts.
 * 2. Selbst in Chrome hing der Ablauf an der Höhe des Abschnitts im Verhältnis
 *    zum Fenster. Bei einem Abschnitt, der ungefähr so hoch ist wie das Fenster,
 *    ist die Eintrittsphase so kurz, dass die Bewegung vorbei war, bevor man
 *    etwas davon sah. Zwei Nachjustierungen später war es immer noch von der
 *    Fenstergröße abhängig – also der falsche Weg.
 *
 * Ein Observer ist kein Scroll-Listener: er meldet sich nur, wenn ein Element
 * die Schwelle über- oder unterschreitet, und läuft nicht bei jedem
 * Scroll-Ereignis im Hauptthread. Das Performance-Argument gegen Scroll-Handler
 * trifft ihn nicht.
 *
 * **Ohne JavaScript bleibt alles sichtbar.** Der versteckte Anfangszustand hängt
 * in `app.css` an `html.js` – der Klasse, die das Inline-Skript im <head> setzt.
 * Kommt das Bundle nicht, fehlt die Klasse, und der Inhalt steht einfach da.
 */

/**
 * Anteil des Elements, der sichtbar sein muss, damit es aufklappt.
 *
 * **Muss 0 bleiben.** Der Wert stand einmal auf 0.12 – und damit blieben
 * Abschnitte, die höher sind als das Fenster, dauerhaft unsichtbar: Von einem
 * 8000 Pixel hohen Abschnitt sind auf einem 844 Pixel hohen Schirm nie 12 %
 * gleichzeitig im Bild, die Schwelle wird also nie überschritten. Auf breiten
 * Schirmen fiel das nicht auf, weil die Abschnitte dort kürzer umbrechen; auf
 * Handy-Breite war `/ki` komplett leer.
 *
 * Den Vorlauf – die Bewegung soll starten, kurz *nachdem* das Element
 * hereingekommen ist – erledigt ohnehin `ROOT_MARGIN`, nicht die Schwelle.
 */
const THRESHOLD = 0;

/**
 * Vorlaufender Rand am unteren Fensterrand: die Bewegung soll starten, kurz
 * *nachdem* das Element hereingekommen ist, nicht schon davor – sonst ist sie
 * wieder vorbei, bevor der Blick dort ankommt. Der negative Wert zieht die
 * Auslöselinie vom unteren Rand nach oben ins Bild.
 */
const ROOT_MARGIN = '0px 0px -12% 0px';

/** Abstand zwischen zwei Karten desselben Behälters, in Millisekunden. */
const STAGGER_MS = 90;

/** Nach so vielen Karten wächst die Verzögerung nicht weiter. */
const STAGGER_MAX = 8;

export function initReveal() {
    const targets = [
        ...document.querySelectorAll('.reveal'),
        ...document.querySelectorAll('.reveal-stagger > *'),
    ];

    if (targets.length === 0) {
        return;
    }

    // Wer „Bewegung reduzieren" eingestellt hat, bekommt den Endzustand sofort –
    // ohne Beobachter, ohne Übergang.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        targets.forEach((el) => el.classList.add('is-revealed'));

        return;
    }

    // Kein Observer im Browser (sehr alt) – dann alles zeigen statt nichts.
    if (!('IntersectionObserver' in window)) {
        targets.forEach((el) => el.classList.add('is-revealed'));

        return;
    }

    // Karten eines Behälters bekommen eine gestaffelte Verzögerung. Über
    // `transition-delay` und nicht über die Reihenfolge im Beobachter: der
    // meldet mehrere Elemente in einem Rutsch, ohne Abstand dazwischen.
    document.querySelectorAll('.reveal-stagger').forEach((group) => {
        Array.from(group.children).forEach((child, index) => {
            const step = Math.min(index, STAGGER_MAX);

            child.style.transitionDelay = `${step * STAGGER_MS}ms`;
        });
    });

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                entry.target.classList.add('is-revealed');

                // Einmal aufgeklappt, bleibt aufgeklappt: ein Element, das beim
                // Zurückscrollen wieder verschwindet, wirkt wie ein Fehler und
                // kostet bei jedem Durchlauf erneut Aufmerksamkeit.
                observer.unobserve(entry.target);
            });
        },
        { threshold: THRESHOLD, rootMargin: ROOT_MARGIN },
    );

    targets.forEach((el) => observer.observe(el));
}
